import { hex, lighten, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { BaseTheme, derivePalette, makeMetrics } from "./BaseTheme.js";
import { drawCheckmark, drawCross } from "./glyphs.js";
import type { CaptionButton, ScrollBarParts, VisualState, WindowVisualState } from "./Theme.js";

/**
 * "Panel" — the curved-block console look: pure black behind everything, a
 * color-coded rail down the left, rounded end-caps on every element and black
 * text on saturated blocks. Named for the shape language rather than the show
 * it evokes, since Webwin is not affiliated with anyone.
 */

const AMBER = hex("#ff9c00");
const TAN = hex("#ffcc99");
const LAVENDER = hex("#cc99cc");
const BLUE = hex("#9999ff");
const SALMON = hex("#ff7c66");
const BLACK = hex("#000000");

const LCARS_FONT = "'Arial Narrow', 'Bahnschrift', 'Segoe UI', sans-serif";

const RAIL_WIDTH = 20;
const GAP = 6;
const TITLE_HEIGHT = 28;

export class LcarsTheme extends BaseTheme {
  constructor() {
    super({
      name: "lcars",
      isDark: true,
      metrics: makeMetrics({
        titleBarHeight: TITLE_HEIGHT,
        windowBorder: 0,
        windowCornerRadius: 0,
        resizeGripSize: 8,
        captionButtonWidth: 40,
        controlCornerRadius: 12,
        controlHeight: 26,
        buttonPadding: insets(16, 4),
        buttonMinWidth: 92,
        focusRingWidth: 2,
        focusRingInset: 2,
        textBoxPadding: insets(12, 4),
        checkBoxSize: 18,
        radioSize: 18,
        scrollBarSize: 16,
        scrollBarMinThumb: 28,
        listItemHeight: 24,
        listPadding: insets(4),
        headerHeight: 26,
        columnPadding: 10,
        font: { family: LCARS_FONT, size: 14, weight: 700 },
        captionFont: { family: LCARS_FONT, size: 15, weight: 700 },
        monoFont: { family: "Consolas, monospace", size: 12.5 },
      }),
      palette: derivePalette({
        desktop: hex("#000000"),
        surface: BLACK,
        raised: AMBER,
        field: hex("#0b0b0b"),
        ink: TAN,
        accent: AMBER,
        accentText: BLACK,
        border: hex("#3a2a10"),
        titleBar: BLACK,
        titleText: AMBER,
        isDark: true,
        overrides: {
          windowBorderActive: BLACK,
          windowBorderInactive: BLACK,
          controlBg: AMBER,
          controlBgHover: lighten(AMBER, 0.25),
          controlBgPressed: TAN,
          controlBgDisabled: hex("#5a4a33"),
          controlBorder: BLACK,
          controlBorderHover: BLACK,
          fieldBorder: LAVENDER,
          fieldBorderFocused: AMBER,
          text: TAN,
          textSubtle: LAVENDER,
          textDisabled: hex("#7a6a55"),
          selectionBg: AMBER,
          selectionText: BLACK,
          selectionInactiveBg: hex("#6b4f22"),
          textSelectionBg: hex("#ff9c0066"),
          scrollTrack: hex("#160f04"),
          scrollThumb: LAVENDER,
          scrollThumbHover: lighten(LAVENDER, 0.2),
          scrollThumbPressed: TAN,
          headerBg: LAVENDER,
          headerBgHover: lighten(LAVENDER, 0.15),
          headerText: BLACK,
          gridLine: hex("#3a2a10"),
          focusRing: BLUE,
          shadow: hex("#00000000"),
        },
      }),
    });
  }

  /** Everything is a pill: the radius is half the control's height. */
  private pill(r: Rect): number {
    return Math.min(r.height, r.width) / 2;
  }

  // --- window chrome ----------------------------------------------------

  override windowClientRect(bounds: Rect, s: WindowVisualState): Rect {
    return {
      x: RAIL_WIDTH + GAP,
      y: this.captionHeight(s) + GAP,
      width: Math.max(0, bounds.width - RAIL_WIDTH - GAP * 2),
      height: Math.max(0, bounds.height - this.captionHeight(s) - GAP * 2),
    };
  }

  override captionButtonRect(
    bounds: Rect,
    button: CaptionButton,
    s: WindowVisualState,
  ): Rect | null {
    const w = this.metrics.captionButtonWidth;
    const order: CaptionButton[] = [];
    if (s.showMinimize) order.push("minimize");
    if (s.showMaximize) order.push("maximize");
    if (s.showClose) order.push("close");
    const index = order.indexOf(button);
    if (index < 0) return null;
    const fromRight = order.length - 1 - index;
    return {
      x: bounds.width - GAP - (fromRight + 1) * (w + 4) + 4,
      y: 2,
      width: w,
      height: this.captionHeight(s) - 4,
    };
  }

  /** Fixed-width label area between the rail and the color blocks. */
  private static readonly TITLE_WIDTH = 150;

  override titleTextRect(bounds: Rect, s: WindowVisualState): Rect {
    return {
      x: RAIL_WIDTH + GAP,
      y: 0,
      width: Math.max(0, Math.min(LcarsTheme.TITLE_WIDTH, bounds.width - RAIL_WIDTH - GAP * 2)),
      height: this.captionHeight(s),
    };
  }

  override drawWindowFrame(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const r: Rect = { x: 0, y: 0, width: bounds.width, height: bounds.height };
    rc.fillRect(r, BLACK);

    const railColor = s.active ? AMBER : hex("#6b4f22");
    // The rail is one long capsule; its top cap forms the window's shoulder.
    rc.fillRect({ x: 0, y: 0, width: RAIL_WIDTH, height: bounds.height }, railColor, RAIL_WIDTH / 2);

    // Caption text occupies the first slot, then color-coded blocks run to the
    // buttons with black gaps between them — the gaps are as much a part of the
    // look as the blocks.
    const title = this.titleTextRect(bounds, s);
    let x = title.x + title.width + 8;

    const firstButton =
      this.captionButtonRect(bounds, "minimize", s) ??
      this.captionButtonRect(bounds, "maximize", s) ??
      this.captionButtonRect(bounds, "close", s);
    const limit = (firstButton?.x ?? bounds.width - GAP) - 6;

    const blocks: { width: number; color: Color }[] = [
      { width: 46, color: s.active ? LAVENDER : hex("#5a4a5a") },
      { width: 22, color: s.active ? SALMON : hex("#5a3a34") },
      { width: 22, color: s.active ? BLUE : hex("#3f3f5a") },
    ];
    for (const block of blocks) {
      if (x + block.width > limit) break;
      rc.fillRect({ x, y: 2, width: block.width, height: this.captionHeight(s) - 4 }, block.color, 8);
      x += block.width + 4;
    }

    // A rule fills whatever caption space the blocks did not.
    if (limit > x + 10) {
      rc.fillRect({ x, y: this.captionHeight(s) - 9, width: limit - x, height: 5 }, railColor, 2.5);
    }

    // A hairline right and bottom edge so a black window still reads as one
    // against a black desktop.
    const edge = s.active ? hex("#4a3410") : hex("#241a08");
    rc.vLine(bounds.width - 1, 0, bounds.height, edge);
    rc.hLine(RAIL_WIDTH, bounds.height - 1, bounds.width - RAIL_WIDTH, edge);

    this.drawCaptionButtons(rc, bounds, s);
  }

  /** Caption buttons are pills too, in their own colors. */
  protected override drawCaptionButtons(
    rc: RenderContext,
    bounds: Rect,
    s: WindowVisualState,
  ): void {
    const colors: Record<CaptionButton, Color> = {
      minimize: BLUE,
      maximize: LAVENDER,
      close: SALMON,
    };
    for (const button of ["minimize", "maximize", "close"] as CaptionButton[]) {
      const r = this.captionButtonRect(bounds, button, s);
      if (!r) continue;
      let fill = s.active ? colors[button] : hex("#4a4a55");
      if (s.pressedButton === button) fill = TAN;
      else if (s.hoveredButton === button) fill = lighten(fill, 0.25);
      rc.fillRect(r, fill, this.pill(r));

      const cx = Math.round(r.x + r.width / 2);
      const cy = Math.round(r.y + r.height / 2);
      switch (button) {
        case "minimize":
          rc.fillRect({ x: cx - 5, y: cy - 1, width: 10, height: 2 }, BLACK);
          break;
        case "maximize":
          rc.strokeRect({ x: cx - 5, y: cy - 4, width: 10, height: 8 }, BLACK, 2);
          break;
        case "close":
          drawCross(rc, r, BLACK, 9);
          break;
      }
    }
  }

  // --- controls ---------------------------------------------------------

  override drawButton(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    let fill: Color;
    if (s.disabled) fill = p.controlBgDisabled;
    else if (s.pressed) fill = TAN;
    else if (s.hovered) fill = lighten(s.isDefault === true ? AMBER : LAVENDER, 0.25);
    else fill = s.isDefault === true ? AMBER : LAVENDER;

    rc.fillRect(r, fill, this.pill(r));
    if (s.focused && !s.disabled) this.drawFocusRing(rc, r);
  }

  /** Black on a saturated block, always. */
  override buttonTextColor(s: VisualState): Color {
    return s.disabled ? hex("#2a2016") : BLACK;
  }

  override drawTextBox(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: s.disabled ? hex("#151515") : p.fieldBg,
      radius: this.pill(r),
      borderWidth: 2,
      borderColor: s.disabled ? hex("#4a4a4a") : s.focused ? AMBER : LAVENDER,
    });
  }

  override drawCheckBox(rc: RenderContext, r: Rect, s: VisualState): void {
    const on = s.checked === true || s.indeterminate === true;
    const fill = s.disabled
      ? hex("#5a4a33")
      : on
        ? s.hovered
          ? lighten(AMBER, 0.2)
          : AMBER
        : s.hovered
          ? hex("#241a08")
          : hex("#0b0b0b");
    rc.drawRect(r, { fill, radius: 4, borderWidth: 2, borderColor: on ? AMBER : LAVENDER });
    if (s.indeterminate) {
      rc.fillRect(
        { x: r.x + 4, y: r.y + r.height / 2 - 1, width: r.width - 8, height: 3 },
        BLACK,
      );
    } else if (s.checked) {
      drawCheckmark(rc, r, BLACK);
    }
  }

  override drawRadioButton(rc: RenderContext, r: Rect, s: VisualState): void {
    const radius = Math.min(r.width, r.height) / 2;
    const on = s.checked === true;
    rc.drawRect(r, {
      fill: s.disabled ? hex("#2a2418") : hex("#0b0b0b"),
      radius,
      borderWidth: 2,
      borderColor: s.disabled ? hex("#5a4a33") : on ? AMBER : LAVENDER,
    });
    if (on) {
      const dot = Math.max(4, Math.round(r.width * 0.5));
      const inset = (r.width - dot) / 2;
      rc.fillRect(
        { x: r.x + inset, y: r.y + inset, width: dot, height: dot },
        s.disabled ? hex("#5a4a33") : AMBER,
        dot / 2,
      );
    }
  }

  override drawPanel(rc: RenderContext, r: Rect, sunken: boolean): void {
    rc.drawRect(r, {
      fill: sunken ? this.palette.fieldBg : BLACK,
      radius: 8,
      borderWidth: 2,
      borderColor: sunken ? LAVENDER : hex("#3a2a10"),
    });
  }

  override drawListBackground(rc: RenderContext, r: Rect, s: VisualState): void {
    rc.drawRect(r, {
      fill: s.disabled ? hex("#151515") : this.palette.fieldBg,
      radius: 8,
      borderWidth: 2,
      borderColor: s.focused ? AMBER : LAVENDER,
    });
  }

  override drawListItem(rc: RenderContext, r: Rect, s: VisualState): void {
    if (s.selected) {
      rc.fillRect(r, s.active === false ? hex("#6b4f22") : AMBER, this.pill(r));
    } else if (s.hovered) {
      rc.fillRect(r, hex("#241a08"), this.pill(r));
    }
  }

  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const fill = s.pressed ? TAN : s.hovered ? lighten(LAVENDER, 0.2) : LAVENDER;
    rc.fillRect({ x: r.x + 1, y: r.y + 2, width: r.width - 2, height: r.height - 4 }, fill, 8);
  }

  override drawScrollBar(rc: RenderContext, parts: ScrollBarParts, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(parts.bounds, p.scrollTrack, 8);
    if (parts.thumb.width <= 0 || parts.thumb.height <= 0) return;
    const pressed = parts.pressedPart === "thumb";
    const hovered = parts.hoveredPart === "thumb" || s.hovered;
    const color = pressed ? TAN : hovered ? lighten(LAVENDER, 0.2) : LAVENDER;
    rc.fillRect(parts.thumb, color, this.pill(parts.thumb));
  }

  override drawFocusRing(rc: RenderContext, r: Rect): void {
    rc.strokeRect(
      { x: r.x + 3, y: r.y + 3, width: r.width - 6, height: r.height - 6 },
      BLUE,
      2,
      Math.max(0, this.pill(r) - 3),
    );
  }
}
